/**
 * The single HTTP client — LV §161.
 *
 * > Components never call `fetch()` directly. Central services only.
 *
 * This is the only module in the frontend that touches `fetch`. Everything else goes
 * through a service in this folder, and each service goes through here. That gives one
 * place for the four things every request needs:
 *
 * 1. **The auth header.** Attached from the token store, never read from a component.
 * 2. **The LV §118 error envelope.** Parsed into `ApiError` so a caller can branch on
 *    `error.code` and show `error.message` — which is already German and user-facing.
 * 3. **The `409` / `422` distinction.** A version conflict (LV §105) and a business-rule
 *    violation (LV §119) need different UI, so they get different predicates rather than
 *    a single "request failed".
 * 4. **The offline signal.** A failed mutation while offline is queued rather than lost
 *    (LV §127-132), and `isOffline` is how the caller knows which it was.
 */

import type { ApiErrorBody } from "@/types/api";

const API_PREFIX = "/api/v1";

/** Requests that take longer than this are treated as failed. */
const DEFAULT_TIMEOUT_MS = 30_000;
/** Uploads get longer — a 50 MB drawing over a plant connection is not a hang. */
const UPLOAD_TIMEOUT_MS = 180_000;

/**
 * A failed API call, carrying the LV §118 envelope.
 *
 * `message` is the backend's German text. The UI shows it directly: the server is the
 * authority on why an action was refused (LV §126), and a frontend that re-worded
 * refusals would eventually disagree with the rule that produced them.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId: string;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> = {},
    requestId = "",
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Optimistic locking failed — the record moved under the user (LV §105, §176). */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** A domain rule refused the action (LV §119). The message explains which. */
  get isBusinessRule(): boolean {
    return this.status === 422;
  }

  /** Not permitted, including a site-scope violation (LV §175). */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * The request never reached the server. Distinguished from a server error because
   * the offline queue only applies to this case — retrying a `422` would fail the same
   * way forever.
   */
  get isOffline(): boolean {
    return this.status === 0;
  }

  /** LV §105's message for the conflict dialog, with the field list when available. */
  get conflictFields(): string[] {
    const fields = this.details["fields"];
    return Array.isArray(fields) ? fields.map(String) : [];
  }
}

type TokenReader = () => string | null;
/** Renews the session after a 401 and resolves whether a fresh token is now held. */
type UnauthorizedHandler = () => Promise<boolean>;

let readToken: TokenReader = () => null;
let onUnauthorized: UnauthorizedHandler = () => Promise.resolve(false);

/**
 * Wire the client to the auth store.
 *
 * Injected rather than imported so this module has no dependency on React or on the
 * store's shape — which is what lets the offline sync engine and the tests use it
 * outside a component tree.
 */
export function configureHttp(options: {
  getToken: TokenReader;
  onUnauthorized: UnauthorizedHandler;
}): void {
  readToken = options.getToken;
  onUnauthorized = options.onUnauthorized;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /**
   * Query parameters. `undefined`, `null` and `""` are dropped; arrays repeat the key.
   *
   * Typed `object` rather than `Record<string, unknown>` so a service can pass its own
   * typed filter interface. An interface has no index signature and would be rejected by
   * `Record`, which would push every call site into a cast.
   */
  query?: object;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Set for multipart uploads: the body is passed through untouched. */
  formData?: FormData;
  /** Returns the raw response — for `/documents/{id}/download`. */
  raw?: boolean;
  /**
   * Extra request headers.
   *
   * One caller today: the sync engine marks what it replays out of the offline queue
   * so the backend can refuse it while `FEATURE_OFFLINE` is off (LV §181). Deliberately
   * not a way to set `Authorization` or `Content-Type` — those are this module's job
   * and are applied after these, so a caller cannot override them by accident.
   */
  headers?: Record<string, string>;
}

function buildQuery(query: object | undefined): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      // Repeated keys rather than a comma list: FastAPI reads `?status=A&status=B`
      // into a list, and a comma would arrive as one string.
      for (const item of value) {
        if (item !== undefined && item !== null && item !== "") {
          params.append(key, String(item));
        }
      }
      continue;
    }
    params.append(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // A non-JSON error body means the failure happened before the application saw
    // the request — a reverse proxy, or a crash. Fall through to a generic message.
  }

  if (body?.error) {
    return new ApiError(
      response.status,
      body.error.code,
      body.error.message,
      body.error.details ?? {},
      body.error.request_id ?? "",
    );
  }

  return new ApiError(
    response.status,
    `HTTP_${response.status}`,
    response.status >= 500
      ? "Der Server hat unerwartet geantwortet. Bitte versuchen Sie es erneut."
      : "Die Anfrage konnte nicht verarbeitet werden.",
    {},
    response.headers.get("X-Request-ID") ?? "",
  );
}

/**
 * Set by the service worker on a GET it answered from its own cache (LV §127, §129).
 *
 * The worker has always set it; nothing read it. What a technician needs to know is not
 * only "there is no network" — the sync pill says that — but "what is on this screen is
 * the state as of the last time there was one". Presenting a cached order list as
 * current is the failure mode Arch §15 rules out for writes, and reads deserve the same
 * honesty.
 */
export const FROM_CACHE_HEADER = "X-From-Cache";

type CacheListener = (fromCache: boolean) => void;
const cacheListeners = new Set<CacheListener>();

/**
 * Be told whether the most recent read was answered from the cache.
 *
 * A signal rather than a return value, because it belongs to the *screen* and not to any
 * one call: a page makes several reads and the answer is the same for all of them, and
 * threading a second value through every service method would touch every caller to
 * inform two of them.
 */
export function onCachedRead(listener: CacheListener): () => void {
  cacheListeners.add(listener);
  return () => cacheListeners.delete(listener);
}

function publishCacheRead(fromCache: boolean): void {
  for (const listener of cacheListeners) listener(fromCache);
}

/** The one place `fetch` is called (LV §161). */
export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return send<T>(path, options, false);
}

async function send<T>(
  path: string,
  options: RequestOptions,
  renewed: boolean,
): Promise<T> {
  const {
    method = "GET",
    body,
    query,
    signal,
    formData,
    raw = false,
    headers: extraHeaders,
    timeoutMs = formData ? UPLOAD_TIMEOUT_MS : DEFAULT_TIMEOUT_MS,
  } = options;

  const url = `${API_PREFIX}${path}${buildQuery(query)}`;

  // Fail fast when the browser already knows it is offline — the same check the sync
  // engine makes as step 2 of Arch §15's sequence. Without it the request sits until the
  // 30 s timeout before the offline queue takes over, and a technician who has just
  // finished a walkdown in a dead spot stares at a spinner for half a minute.
  //
  // Only the negative is trusted: `navigator.onLine === false` means there is no network,
  // whereas `true` says nothing about whether the server is reachable (a captive portal
  // reports online), so the normal path still has to handle a failing fetch.
  //
  // **Mutations only, and that restriction is the whole point.** Applied to reads as
  // well, this short-circuit never called `fetch`, so the service worker was never
  // consulted — and the service worker is what serves the LV §127 read endpoints from
  // its cache. The work-order list was cached on every online visit and could not be
  // displayed by the one thing the cache exists for. A read has nothing to queue and
  // nothing to lose by trying: with no network the `fetch` rejects immediately rather
  // than after the timeout, because the browser does not open a socket it knows cannot
  // connect.
  if (
    method !== "GET" &&
    typeof navigator !== "undefined" &&
    navigator.onLine === false
  ) {
    throw new ApiError(
      0,
      "NETWORK_UNAVAILABLE",
      "Keine Verbindung zum Server. Änderungen werden lokal gespeichert und später übertragen.",
      { reason: "navigator.onLine" },
    );
  }

  // Caller headers first, so the two below always win: auth is not negotiable here.
  const headers = new Headers(extraHeaders);
  const token = readToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (body !== undefined && !formData) headers.set("Content-Type", "application/json");

  // The timeout controller is combined with the caller's signal so a component
  // unmounting and a slow server both abort the same request.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  signal?.addEventListener("abort", () => controller.abort(), { once: true });

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
      signal: controller.signal,
      // Cookie-based sessions use Secure/HttpOnly/SameSite (Arch §16), so
      // credentials travel for the session variant as well as the bearer one.
      credentials: "same-origin",
    });
  } catch (cause) {
    clearTimeout(timeout);
    if (signal?.aborted) throw cause;
    // Status 0 marks "never reached the server", which is what the offline queue
    // keys on. A server that answered 500 is a different situation.
    throw new ApiError(
      0,
      "NETWORK_UNAVAILABLE",
      "Keine Verbindung zum Server. Änderungen werden lokal gespeichert und später übertragen.",
      { cause: String(cause) },
    );
  } finally {
    clearTimeout(timeout);
  }

  if (method === "GET") {
    publishCacheRead(response.headers.get(FROM_CACHE_HEADER) === "true");
  }

  if (response.status === 401) {
    // Renew, then repeat the request — once (#411). Without the repeat the page stayed
    // broken even when the renewal succeeded, which users met as "manchmal muss ich neu
    // laden". Repeating is safe for a mutation too: the server answers 401 from its
    // authentication dependency, before any handler has run, so nothing happened that a
    // second attempt could do twice. And only once: a request refused again with a freshly
    // renewed token is refused for a reason renewing cannot fix.
    if (!renewed && (await onUnauthorized())) return send<T>(path, options, true);
    throw await toApiError(response);
  }
  if (!response.ok) throw await toApiError(response);

  if (raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("application/json")) return undefined as T;
  return (await response.json()) as T;
}

export const http = {
  get: <T>(path: string, query?: object, signal?: AbortSignal) =>
    request<T>(path, { method: "GET", query, signal }),

  post: <T>(path: string, body?: unknown, query?: object) =>
    request<T>(path, { method: "POST", body, query }),

  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body }),

  /**
   * Replace one named part of a record.
   *
   * Added for LV §58's hazard assessment, which is a `PUT` rather than a `PATCH`
   * because it sets the whole reference — document, confirmation and note travel
   * together, and clearing it is sending `null` rather than omitting a field.
   */
  put: <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body }),

  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "DELETE", body }),

  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: "POST", formData }),

  download: (path: string) => request<Response>(path, { method: "GET", raw: true }),
};

/** Health probes sit outside `/api/v1` so monitoring need not know the version (LV §142). */
export async function health(): Promise<{ status: string }> {
  const response = await fetch("/health/ready", { credentials: "same-origin" });
  return (await response.json()) as { status: string };
}
